import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  fetchCompanyDashboardSummary,
  fetchCompanyDrivers,
  fetchCompanyReviewMatches,
  fetchMyCompany,
  logoutCompanyOwnerAuthSession,
  reviewCompanyMatch,
} from '../services/api/companyOwnerAuth';
import {
  clearCompanyOwnerAuthFlow,
  getCompanyOwnerAuthUser,
  getCompanyOwnerCompany,
  saveCompanyOwnerCompany,
} from '../services/storage/companyOwnerFlow';
import {
  CompanyDriverListResponse,
  CompanyLinkReviewItem,
  CompanyOwnerCompany,
  CompanyOwnerDashboardSummary,
} from '../types/companyOwner';
import {
  formatCurrency,
  formatDateTime,
  getNonCompanyAccessFallbackRoute,
  formatRelativeStatusTime,
  isCompanyOwnerUser,
  requiresCompanyPayoutSetup,
} from '../utils/companyOwner';
import { chartNumber } from '../utils/chartValue';
import '../styles/company-owner.css';
import '../styles/register.css';

const STATUS_CHART_COLORS = ['#0066ff', '#0ea5e9', '#f59e0b', '#94a3b8'];

const CompanyOwnerDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const companyOwnerUser = useMemo(() => getCompanyOwnerAuthUser(), []);
  const [company, setCompany] = useState<CompanyOwnerCompany | null>(getCompanyOwnerCompany());
  const [summary, setSummary] = useState<CompanyOwnerDashboardSummary | null>(null);
  const [drivers, setDrivers] = useState<CompanyDriverListResponse | null>(null);
  const [reviewItems, setReviewItems] = useState<CompanyLinkReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [driverQuery, setDriverQuery] = useState('');
  const [debouncedDriverQuery, setDebouncedDriverQuery] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [onTripOnly, setOnTripOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [reviewLoadingId, setReviewLoadingId] = useState<string | null>(null);
  const hasLoadedInitially = useRef(false);

  const handleLogout = async () => {
    try {
      await logoutCompanyOwnerAuthSession();
    } catch {
      clearCompanyOwnerAuthFlow();
    }
    navigate('/', { replace: true });
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedDriverQuery(driverQuery.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [driverQuery]);

  const loadDashboardData = useCallback(
    async (showFullLoader: boolean) => {
      if (!isCompanyOwnerUser(companyOwnerUser)) {
        clearCompanyOwnerAuthFlow();
        navigate(getNonCompanyAccessFallbackRoute(), { replace: true });
        return;
      }

      if (showFullLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError('');

      try {
        const resolvedCompany = await fetchMyCompany();
        if (!resolvedCompany) {
          navigate('/company/onboarding', { replace: true });
          return;
        }

        saveCompanyOwnerCompany(resolvedCompany);
        setCompany(resolvedCompany);

        const [nextSummary, nextDrivers, nextReviewItems] = await Promise.all([
          fetchCompanyDashboardSummary(),
          fetchCompanyDrivers({
            ...(debouncedDriverQuery ? { query: debouncedDriverQuery } : {}),
            ...(onlineOnly ? { online: true } : {}),
            ...(onTripOnly ? { onTrip: true } : {}),
            linkStatus: 'LINKED',
            page,
            size: 10,
          }),
          fetchCompanyReviewMatches(),
        ]);

        setSummary(nextSummary);
        setDrivers(nextDrivers);
        setReviewItems(nextReviewItems);
      } catch (fetchError: any) {
        setError(fetchError.message || 'Could not load the company dashboard.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [companyOwnerUser, debouncedDriverQuery, navigate, onlineOnly, onTripOnly, page],
  );

  useEffect(() => {
    const showFullLoader = !hasLoadedInitially.current;
    void loadDashboardData(showFullLoader);
    hasLoadedInitially.current = true;
  }, [loadDashboardData]);

  const handleReview = async (linkId: string, decision: 'CONFIRM' | 'REJECT') => {
    setReviewLoadingId(linkId);
    setError('');

    try {
      await reviewCompanyMatch({ linkId, decision });
      await loadDashboardData(false);
    } catch (reviewError: any) {
      setError(reviewError.message || 'Could not update the driver-company review.');
    } finally {
      setReviewLoadingId(null);
    }
  };

  const earningsChartData = useMemo(() => {
    return [
      { label: 'Today', value: summary?.earningsToday ?? 0 },
      { label: 'Week', value: summary?.earningsThisWeek ?? 0 },
      { label: 'Month', value: summary?.earningsThisMonth ?? 0 },
    ].map((point) => ({
      ...point,
      value:
        typeof point.value === 'number' ? point.value : Number.parseFloat(String(point.value || 0)),
    }));
  }, [summary]);

  const topDrivers = useMemo(() => {
    return [...(drivers?.items ?? [])]
      .sort((left, right) => (right.monthlyEarnings ?? 0) - (left.monthlyEarnings ?? 0))
      .slice(0, 5);
  }, [drivers]);

  const topDriverChartData = useMemo(
    () =>
      topDrivers.map((driver) => ({
        name: driver.name.split(' ')[0] || driver.name,
        value: driver.monthlyEarnings ?? 0,
      })),
    [topDrivers],
  );

  const statusChartData = useMemo(() => {
    const totalDrivers = summary?.totalLinkedDrivers ?? 0;
    const onlineDrivers = summary?.onlineDrivers ?? 0;
    const onTripDrivers = summary?.activeDriversNow ?? 0;
    const standbyDrivers = Math.max(onlineDrivers - onTripDrivers, 0);
    const offlineDrivers = Math.max(totalDrivers - onlineDrivers, 0);
    const reviewDrivers = (company?.pendingReviewLinks ?? 0) + (company?.manualReviewLinks ?? 0);

    return [
      { name: 'On trip', value: onTripDrivers },
      { name: 'Online', value: standbyDrivers },
      { name: 'Offline', value: offlineDrivers },
      { name: 'Review', value: reviewDrivers },
    ].filter((item) => item.value > 0);
  }, [company, summary]);

  const payoutSetupRequired = requiresCompanyPayoutSetup(summary?.payoutSummary?.payoutStatus);
  const showPayoutSetupBanner =
    Boolean(summary?.payoutSummary) && summary?.payoutSummary?.payoutStatus !== 'ACTIVE';
  const payoutSummary = summary?.payoutSummary;
  const settlementReconciliationAvailable = Boolean(
    payoutSummary?.settlementReconciliationAvailable,
  );

  if (loading) {
    return (
      <section className="register-page">
        <div className="register-shell register-shell-single">
          <div className="register-panel register-panel-form register-status-panel">
            <div className="register-loader" />
            <h1 className="register-form-title">Loading company dashboard</h1>
            <p className="register-form-copy">
              Pulling your linked drivers, performance summaries, review items, and settlement view.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="company-page">
      <div className="company-dashboard-shell">
        <div className="company-dashboard-header">
          <div>
            <div className="register-form-badge">Company Dashboard</div>
            <h1 className="company-dashboard-title">
              {company?.tradingName || company?.legalName || 'Company operations'}
            </h1>
            <p className="company-dashboard-copy">
              Monitor linked drivers, trip activity, earnings visibility, and company matching work
              from one place.
            </p>
          </div>

          <div className="company-dashboard-actions">
            <Link className="register-secondary-button" to="/company/payout-setup">
              Payout details
            </Link>
            <Link className="register-secondary-button" to="/">
              Home
            </Link>
            <button
              className="register-secondary-button"
              onClick={() => void loadDashboardData(false)}
              type="button"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              className="register-secondary-button"
              onClick={handleLogout}
              type="button"
            >
              Logout
            </button>
          </div>
        </div>

        {error ? <div className="register-inline-error">{error}</div> : null}

        {showPayoutSetupBanner ? (
          <section className="company-empty-hero">
            <div>
              <div className="company-empty-hero-badge">
                {payoutSetupRequired ? 'Payout setup required' : 'Payout setup status'}
              </div>
              <h2>
                {payoutSetupRequired
                  ? 'Complete company payout details'
                  : 'Company payout details submitted'}
              </h2>
              <p>
                {/* Optional-chained to match how `payoutSummary` is read
                    everywhere else on this page. `showPayoutSetupBanner` already
                    proves both links are present, but it is a boolean rather
                    than a type guard, so the narrowing does not reach here. The
                    fallback string was always the behaviour when the note was
                    empty; this just makes a null summary take the same path. */}
                {summary?.payoutSummary?.note ||
                  'Settlement status for company-linked driver payouts will appear here.'}
              </p>
            </div>
            <div className="company-empty-hero-actions">
              <Link className="register-submit register-submit-inline" to="/company/payout-setup">
                {payoutSetupRequired ? 'Add payout details' : 'Review payout status'}
              </Link>
            </div>
          </section>
        ) : null}

        {summary && (summary.totalLinkedDrivers ?? 0) === 0 ? (
          <section className="company-empty-hero">
            <div>
              <div className="company-empty-hero-badge">New company dashboard</div>
              <h2>No linked drivers yet</h2>
              <p>
                Your company profile is ready. As company drivers register with matching company
                details, they will appear here once linked or flagged for review.
              </p>
            </div>
            <div className="company-empty-hero-actions">
              <button
                className="register-secondary-button"
                onClick={() => void loadDashboardData(false)}
                type="button"
              >
                {refreshing ? 'Refreshing...' : 'Refresh dashboard'}
              </button>
              <button
                className="register-submit register-submit-inline"
                onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                type="button"
              >
                Review possible matches
              </button>
            </div>
          </section>
        ) : null}

        <div className="company-stats-grid">
          <div className="company-stat-card">
            <span className="company-stat-label">Linked Drivers</span>
            <strong>{summary?.totalLinkedDrivers ?? 0}</strong>
            <span className="company-stat-note">
              {company?.pendingReviewLinks ?? 0} pending review, {company?.manualReviewLinks ?? 0}{' '}
              manual review
            </span>
          </div>
          <div className="company-stat-card">
            <span className="company-stat-label">Online Now</span>
            <strong>{summary?.onlineDrivers ?? 0}</strong>
            <span className="company-stat-note">
              {summary?.activeDriversNow ?? 0} currently on active trips
            </span>
          </div>
          <div className="company-stat-card">
            <span className="company-stat-label">Trips Today</span>
            <strong>{summary?.tripsToday ?? 0}</strong>
            <span className="company-stat-note">
              {summary?.driversWithTripsToday ?? 0} drivers with work scheduled or completed today
            </span>
          </div>
          <div className="company-stat-card">
            <span className="company-stat-label">Earned This Month</span>
            <strong>{formatCurrency(summary?.earningsThisMonth)}</strong>
            <span className="company-stat-note">Completed net earnings, not confirmed settled funds</span>
          </div>
        </div>

        {payoutSummary ? (
          <section className="company-settlement-panel">
            <div className="company-settlement-header">
              <div>
                <h2 className="company-panel-title">Settlement view</h2>
                <p className="company-panel-copy">
                  Paystack settles split payments after its working-day processing window. Completed
                  earnings and settled money are shown separately.
                </p>
              </div>
              <span
                className={
                  settlementReconciliationAvailable
                    ? 'company-status-pill company-status-pill-active'
                    : 'company-status-pill company-status-pill-pending'
                }
              >
                {settlementReconciliationAvailable ? 'Reconciled' : 'Settlement sync pending'}
              </span>
            </div>

            <div className="company-settlement-grid">
              <div className="company-settlement-metric">
                <span>Completed earnings</span>
                <strong>
                  {formatCurrency(payoutSummary.earnedThisMonth ?? summary?.earningsThisMonth)}
                </strong>
                <small>Net company-driver earnings recorded this month.</small>
              </div>
              <div className="company-settlement-metric">
                <span>Pending Paystack settlement</span>
                <strong>{formatCurrency(payoutSummary.estimatedPendingSettlement)}</strong>
                <small>Split-applied funds not yet confirmed by settlement reconciliation.</small>
              </div>
              <div className="company-settlement-metric">
                <span>Confirmed settled</span>
                <strong>{formatCurrency(payoutSummary.confirmedSettledThisMonth)}</strong>
                <small>
                  {settlementReconciliationAvailable
                    ? 'Confirmed against settlement records.'
                    : 'Unavailable until Paystack settlement reconciliation is enabled.'}
                </small>
              </div>
              <div className="company-settlement-metric">
                <span>On hold / exceptions</span>
                <strong>
                  {formatCurrency(
                    Number(payoutSummary.onHoldAmount ?? 0) +
                      Number(payoutSummary.exceptionAmount ?? 0),
                  )}
                </strong>
                <small>Refunds, disputes, holds, or failed payout states.</small>
              </div>
            </div>

            {payoutSummary.note ? (
              <p className="company-settlement-note">{payoutSummary.note}</p>
            ) : null}
          </section>
        ) : null}

        <div className="company-main-grid">
          <div className="company-main-column">
            <section className="company-panel">
              <div className="company-panel-header">
                <div>
                  <h2 className="company-panel-title">Earnings snapshot</h2>
                  <p className="company-panel-copy">
                    A fast visual read of completed net earnings by activity window.
                  </p>
                </div>
                <span
                  className={`company-status-pill company-status-pill-${(company?.status || 'PENDING').toLowerCase()}`}
                >
                  {company?.status || 'PENDING'}
                </span>
              </div>

              <div className="company-chart-grid">
                <div className="company-chart-card">
                  <div className="company-chart-card-header">
                    <div>
                      <strong>Earnings trend</strong>
                      <span>Completed earnings across the core time windows.</span>
                    </div>
                  </div>
                  <div className="company-chart-surface">
                    <ResponsiveContainer height={240} width="100%">
                      <AreaChart data={earningsChartData}>
                        <defs>
                          <linearGradient id="companyEarningsFill" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="5%" stopColor="#0066ff" stopOpacity={0.32} />
                            <stop offset="95%" stopColor="#0066ff" stopOpacity={0.03} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="#e7effc" strokeDasharray="4 4" vertical={false} />
                        <XAxis axisLine={false} dataKey="label" tickLine={false} stroke="#6b7f9f" />
                        <Tooltip
                          formatter={(value) => formatCurrency(chartNumber(value))}
                          labelStyle={{ color: '#0f1a38', fontWeight: 700 }}
                        />
                        <Area
                          dataKey="value"
                          fill="url(#companyEarningsFill)"
                          stroke="#0066ff"
                          strokeWidth={3}
                          type="monotone"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="company-chart-card">
                  <div className="company-chart-card-header">
                    <div>
                      <strong>Fleet status</strong>
                      <span>Live split between on-trip, online, offline, and review states.</span>
                    </div>
                  </div>
                  {statusChartData.length ? (
                    <div className="company-chart-surface company-chart-surface-tight">
                      <ResponsiveContainer height={240} width="100%">
                        <PieChart>
                          <Pie
                            cx="50%"
                            cy="50%"
                            data={statusChartData}
                            dataKey="value"
                            innerRadius={52}
                            outerRadius={82}
                            paddingAngle={3}
                          >
                            {statusChartData.map((entry, index) => (
                              <Cell
                                fill={STATUS_CHART_COLORS[index % STATUS_CHART_COLORS.length]}
                                key={entry.name}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => {
                              const drivers = chartNumber(value);
                              return drivers === null
                                ? '—'
                                : `${drivers} driver${drivers === 1 ? '' : 's'}`;
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="company-chart-legend">
                        {statusChartData.map((item, index) => (
                          <div className="company-chart-legend-item" key={item.name}>
                            <span
                              className="company-chart-legend-dot"
                              style={{
                                backgroundColor:
                                  STATUS_CHART_COLORS[index % STATUS_CHART_COLORS.length],
                              }}
                            />
                            <span>{item.name}</span>
                            <strong>{item.value}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="company-empty-card company-empty-card-compact">
                      <div className="company-empty-card-icon">S</div>
                      <div className="company-empty-card-content">
                        <strong>Fleet status chart will appear here</strong>
                        <p>
                          Once linked drivers begin moving through online and trip states, this
                          visual split will update automatically.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="company-panel">
              <div className="company-panel-header">
                <div>
                  <h2 className="company-panel-title">Company drivers</h2>
                  <p className="company-panel-copy">Search only inside the drivers linked to this company.</p>
                </div>
              </div>

              <div className="company-driver-filters">
                <input
                  className="register-input"
                  onChange={(event) => {
                    setDriverQuery(event.target.value);
                    setPage(0);
                  }}
                  placeholder="Search by driver name, email, or raw company name"
                  value={driverQuery}
                />
                <label className="company-checkbox">
                  <input
                    checked={onlineOnly}
                    onChange={(event) => {
                      setOnlineOnly(event.target.checked);
                      setPage(0);
                    }}
                    type="checkbox"
                  />
                  <span>Online only</span>
                </label>
                <label className="company-checkbox">
                  <input
                    checked={onTripOnly}
                    onChange={(event) => {
                      setOnTripOnly(event.target.checked);
                      setPage(0);
                    }}
                    type="checkbox"
                  />
                  <span>On trip only</span>
                </label>
              </div>

              <div className="company-drivers-table">
                <div className="company-drivers-head">
                  <span>Driver</span>
                  <span>Status</span>
                  <span>Today</span>
                  <span>This Week</span>
                  <span>Completed</span>
                </div>
                {(drivers?.items ?? []).map((driver) => (
                  <button
                    className="company-driver-row"
                    key={driver.driverId}
                    onClick={() => navigate(`/company/drivers/${driver.driverId}`)}
                    type="button"
                  >
                    <span>
                      <strong>{driver.name}</strong>
                      <small>{driver.email}</small>
                    </span>
                    <span>
                      <strong>{driver.onTrip ? 'On trip' : driver.online ? 'Online' : 'Offline'}</strong>
                      <small>{driver.linkStatus}</small>
                    </span>
                    <span>{formatCurrency(driver.dailyEarnings)}</span>
                    <span>{formatCurrency(driver.weeklyEarnings)}</span>
                    <span>{driver.completedTrips ?? 0}</span>
                  </button>
                ))}
                {!drivers?.items?.length ? (
                  <div className="company-empty-card">
                    <div className="company-empty-card-icon">D</div>
                    <div className="company-empty-card-content">
                      <strong>No linked drivers match this view yet</strong>
                      <p>
                        Try clearing the filters, refresh the dashboard, or check the review queue
                        below for company-driver records that still need owner approval.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="company-pagination">
                <button
                  className="register-secondary-button"
                  disabled={page === 0}
                  onClick={() => setPage((current) => Math.max(current - 1, 0))}
                  type="button"
                >
                  Previous
                </button>
                <span>
                  Page {page + 1} of{' '}
                  {Math.max(Math.ceil((drivers?.total ?? 0) / (drivers?.size || 10)), 1)}
                </span>
                <button
                  className="register-secondary-button"
                  disabled={((page + 1) * (drivers?.size || 10)) >= (drivers?.total ?? 0)}
                  onClick={() => setPage((current) => current + 1)}
                  type="button"
                >
                  Next
                </button>
              </div>
            </section>
          </div>

          <div className="company-side-column">
            <section className="company-panel">
              <div className="company-panel-header">
                <div>
                  <h2 className="company-panel-title">Top visible performers</h2>
                  <p className="company-panel-copy">
                    A quick ranking from the currently loaded company drivers.
                  </p>
                </div>
              </div>

              {topDriverChartData.length ? (
                <div className="company-chart-surface company-chart-surface-top-drivers">
                  <ResponsiveContainer height={220} width="100%">
                    <BarChart data={topDriverChartData} layout="vertical">
                      <CartesianGrid horizontal={false} stroke="#e7effc" strokeDasharray="4 4" />
                      <XAxis axisLine={false} stroke="#6b7f9f" tickLine={false} type="number" />
                      <YAxis
                        axisLine={false}
                        dataKey="name"
                        stroke="#6b7f9f"
                        tickLine={false}
                        type="category"
                        width={72}
                      />
                      <Tooltip formatter={(value) => formatCurrency(chartNumber(value))} />
                      <Bar dataKey="value" fill="#0066ff" radius={[0, 10, 10, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : null}

              <div className="company-leaderboard">
                {topDrivers.map((driver, index) => (
                  <div className="company-leaderboard-row" key={driver.driverId}>
                    <span className="company-leaderboard-rank">#{index + 1}</span>
                    <div>
                      <strong>{driver.name}</strong>
                      <small>{formatRelativeStatusTime(driver.lastSeenAt)}</small>
                    </div>
                    <span>{formatCurrency(driver.monthlyEarnings)}</span>
                  </div>
                ))}
                {!topDrivers.length ? (
                  <div className="company-empty-card company-empty-card-compact">
                    <div className="company-empty-card-icon">P</div>
                    <div className="company-empty-card-content">
                      <strong>Performance rankings will appear here</strong>
                      <p>
                        Once linked drivers start showing up, this area will highlight your top
                        visible earners first.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="company-panel">
              <div className="company-panel-header">
                <div>
                  <h2 className="company-panel-title">Review matches</h2>
                  <p className="company-panel-copy">Confirm or reject ambiguous company-driver links.</p>
                </div>
              </div>

              <div className="company-review-list">
                {reviewItems.map((item) => (
                  <div className="company-review-card" key={item.linkId}>
                    <strong>{item.driverName}</strong>
                    <span>{item.driverEmail}</span>
                    <span>
                      Raw company: {item.rawCompanyName || 'Not supplied'} | confidence{' '}
                      {item.matchingConfidence ?? 0}
                    </span>
                    <p>{item.matchingNotes || 'Review this link carefully before confirming it.'}</p>
                    <div className="company-review-actions">
                      <button
                        className="register-submit register-submit-inline"
                        disabled={reviewLoadingId === item.linkId}
                        onClick={() => void handleReview(item.linkId, 'CONFIRM')}
                        type="button"
                      >
                        {reviewLoadingId === item.linkId ? 'Saving...' : 'Confirm'}
                      </button>
                      <button
                        className="register-secondary-button"
                        disabled={reviewLoadingId === item.linkId}
                        onClick={() => void handleReview(item.linkId, 'REJECT')}
                        type="button"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
                {!reviewItems.length ? (
                  <div className="company-empty-card company-empty-card-compact">
                    <div className="company-empty-card-icon">R</div>
                    <div className="company-empty-card-content">
                      <strong>No company-driver links need review right now</strong>
                      <p>
                        Ambiguous matches will appear here when the backend needs your confirmation
                        before linking drivers to this company.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyOwnerDashboardPage;
